/**
 * Pure data helpers shared by the journey definitions and the shell. These
 * read the canonical server contracts (comparison engine, insight spine, cash
 * planning) and shape them for narration / charts / takeaways. No financial
 * logic is re-derived here — selection and formatting only.
 */

import type {
  CfoInsightsResponse,
  InsightCard,
  MetricComparison,
  MetricComparisonKind,
  MetricComparisonResponse,
  MetricComparisonRow,
  QuickBooksCashPlanningResponse,
  QuickBooksCashPlanningWeekSummary,
} from '../../../data/ServerApiClient'
import type { JourneyTakeaway } from './types'

export const num = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const formatCurrencyCompact = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

export const formatPercent = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

export const formatSignedPct = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

export const formatSignedCurrency = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${formatCurrencyCompact(Math.abs(value))}`
}

/** Find a metric row tolerant of registry id drift (matches the Brief). */
export const findMetricRow = (
  resp: MetricComparisonResponse | null,
  id: string,
): MetricComparisonRow | null => {
  if (!resp) return null
  const exact = resp.metrics.find((m) => m.id === id)
  if (exact) return exact
  const lowered = id.toLowerCase()
  return (
    resp.metrics.find(
      (m) => m.id.toLowerCase() === lowered || m.label.toLowerCase().includes(lowered),
    ) || null
  )
}

export const pickComparison = (
  row: MetricComparisonRow | null,
  kind: MetricComparisonKind,
): MetricComparison | null => row?.comparisons.find((c) => c.kind === kind) || null

/**
 * Build a coarse oldest -> newest trajectory for a metric from the comparison
 * windows (YoY prior -> L12M prior -> QoQ prior -> L3M prior -> MoM prior ->
 * latest). The comparison contract carries prior/current per window rather than
 * a full monthly series, so this gives a directionally-correct mini trend.
 */
export const coarseTrajectory = (row: MetricComparisonRow | null): Array<number | null> => {
  if (!row) return []
  const ordered: Array<number | null> = [
    pickComparison(row, 'yoy')?.prior_value ?? null,
    pickComparison(row, 'l12m_vs_prior12m')?.prior_value ?? null,
    pickComparison(row, 'qoq')?.prior_value ?? null,
    pickComparison(row, 'l3m_vs_prior3m')?.prior_value ?? null,
    pickComparison(row, 'mom')?.prior_value ?? null,
    row.latest_value ?? null,
  ]
  return ordered.filter((v): v is number => v != null && Number.isFinite(v))
}

export const COARSE_LABELS = ['−12mo', '−6mo', '−3mo', '−2mo', 'Prior', 'Latest']

/** Trajectory + trimmed labels (one label per populated point, newest-aligned). */
export const trajectoryWithLabels = (
  row: MetricComparisonRow | null,
): { values: number[]; labels: string[] } => {
  const values = coarseTrajectory(row) as number[]
  const labels = COARSE_LABELS.slice(COARSE_LABELS.length - values.length)
  return { values, labels }
}

/** Convert an insight card to a mini-takeaway. */
export const cardToTakeaway = (card: InsightCard): JourneyTakeaway => ({
  id: card.id,
  severity: card.severity,
  headline: card.headline,
  body: card.body,
})

/**
 * Select insight cards for a step by tag/detector/metric keywords, preserving
 * the server's deterministic ranking. Falls back to the top-ranked cards when
 * nothing matches so a step never renders empty.
 */
export const selectInsights = (
  insights: CfoInsightsResponse | null,
  match: { tags?: string[]; detectors?: string[]; metrics?: string[] },
  fallbackLimit = 3,
): InsightCard[] => {
  if (!insights || insights.cards.length === 0) return []
  const tags = (match.tags || []).map((t) => t.toLowerCase())
  const detectors = (match.detectors || []).map((d) => d.toLowerCase())
  const metrics = (match.metrics || []).map((m) => m.toLowerCase())
  const matched = insights.cards.filter((card) => {
    const cardTags = (card.tags || []).map((t) => t.toLowerCase())
    if (tags.length && cardTags.some((t) => tags.includes(t))) return true
    if (detectors.length && detectors.includes(card.detector.toLowerCase())) return true
    if (metrics.length && card.impact.metric_id && metrics.includes(card.impact.metric_id.toLowerCase())) return true
    return false
  })
  if (matched.length > 0) return matched.slice(0, Math.max(fallbackLimit, matched.length))
  return insights.cards.slice(0, fallbackLimit)
}

/** Up to 13 forward weeks of cash, ordered by week start. */
export const cashWeeks = (
  resp: QuickBooksCashPlanningResponse | null,
): QuickBooksCashPlanningWeekSummary[] => {
  if (!resp || !Array.isArray(resp.weeks)) return []
  return [...resp.weeks]
    .sort((a, b) => String(a.week_start).localeCompare(String(b.week_start)))
    .slice(0, 13)
}

export const formatWeekLabel = (weekStart: string | null | undefined): string => {
  const raw = String(weekStart || '').trim()
  if (!raw) return '—'
  const parsed = new Date(`${raw}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export const formatAsOfMonth = (asOf: string | null | undefined): string => {
  const raw = String(asOf || '').trim()
  if (!raw) return 'the latest month'
  const parsed = new Date(`${raw}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
